/**
 * Assistant-message markdown rendering (P1-058d).
 *
 * The API contract keeps `AssistantResponse.message` as a plain string;
 * this module parses a conservative GFM subset client-side, sanitizes the
 * HTML, and returns a Lit template for binding inside assistant bubbles.
 */
import DOMPurify from 'dompurify';
import { html, type TemplateResult } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'b',
  'i',
  'ul',
  'ol',
  'li',
  'a',
  'code',
  'pre',
  'h1',
  'h2',
  'h3',
  'h4',
  'blockquote',
] as const;

const ALLOWED_ATTR = ['href', 'target', 'rel', 'class'] as const;

/** http(s), mailto, tel only — blocks javascript: and data: URLs. */
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|tel:)/i;

let domPurifyHooksInstalled = false;

function installDomPurifyHooks(): void {
  if (domPurifyHooksInstalled) {
    return;
  }
  domPurifyHooksInstalled = true;

  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName === 'href') {
      const href = data.attrValue.trim();
      if (!/^(?:https?:|mailto:|tel:)/i.test(href)) {
        data.keepAttr = false;
      }
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isSafeHref(href: string): boolean {
  return /^(?:https?:|mailto:|tel:)/i.test(href.trim());
}

marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    link({ href, text }) {
      const url = href ?? '';
      if (!isSafeHref(url)) {
        return escapeHtml(text);
      }
      return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`;
    },
  },
});

installDomPurifyHooks();

/**
 * Parse assistant markdown to sanitized HTML suitable for `unsafeHTML`.
 * Exported for unit tests.
 */
export function parseAssistantMarkdownToHtml(markdown: string): string {
  const trimmed = markdown.trim();
  if (trimmed.length === 0) {
    return '';
  }

  const raw = marked.parse(trimmed, { async: false }) as string;
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    ALLOWED_URI_REGEXP,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
  });
}

/**
 * Lit template for a completed (non-streaming) assistant bubble body.
 */
export function renderAssistantMarkdownBody(markdown: string): TemplateResult {
  const htmlString = parseAssistantMarkdownToHtml(markdown);
  if (htmlString.length === 0) {
    return html`<div class="bubble__markdown"></div>`;
  }
  return html`<div class="bubble__markdown">${unsafeHTML(htmlString)}</div>`;
}
